import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { adminAPI } from '../../utils/api';

const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('de-DE'); } catch (e) { return d; }
};

const DashboardTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getStats();
      if (res.success) setData(res);
    } catch (e) {
      toast.error('Fehler beim Laden der Kennzahlen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (id, paid) => {
    try {
      await adminAPI.markPaid(id, paid);
      toast.success(paid ? 'Als bezahlt markiert' : 'Als offen markiert');
      load();
    } catch (e) {
      toast.error('Konnte Status nicht ändern');
    }
  };

  const exportMembers = async (format) => {
    try {
      const res = await adminAPI.getMembers();
      if (!res.success) return;
      const rows = res.members.map((m) => ({
        Nachname: m.last_name || '',
        Vorname: m.first_name || '',
        Benutzername: m.username || '',
        'E-Mail': m.email || '',
        Telefon: m.phone || '',
        'EFC-Nr': m.efc_number || '',
        Rolle: m.role || '',
        Status: m.status || '',
        'E-Mail bestätigt': m.email_verified ? 'ja' : 'nein',
        Registriert: fmtDate(m.created_at),
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Mitglieder');
      const stamp = new Date().toISOString().slice(0, 10);
      if (format === 'csv') {
        XLSX.writeFile(wb, `EFC-Mitglieder_${stamp}.csv`, { bookType: 'csv' });
      } else {
        XLSX.writeFile(wb, `EFC-Mitglieder_${stamp}.xlsx`);
      }
      toast.success(`Mitglieder als ${format.toUpperCase()} exportiert`);
    } catch (e) {
      toast.error('Export fehlgeschlagen');
    }
  };

  if (loading) return <div className="loading">Lade Kennzahlen…</div>;
  if (!data) return <div className="loading">Keine Daten.</div>;

  const s = data.stats;

  return (
    <div className="dashboard-tab">
      {/* KPI-Kacheln */}
      <div className="kpi-grid">
        <div className="kpi-card"><div className="kpi-value">{s.members}</div><div className="kpi-label">Mitglieder</div></div>
        <div className="kpi-card"><div className="kpi-value">{s.pendingMembers}</div><div className="kpi-label">Wartend auf Freischaltung</div></div>
        <div className="kpi-card"><div className="kpi-value">{s.upcomingGames}</div><div className="kpi-label">Kommende Spiele</div></div>
        <div className="kpi-card"><div className="kpi-value">{s.recentRegistrationsCount}</div><div className="kpi-label">Neue Anmeldungen (7 Tage)</div></div>
        <div className={`kpi-card ${s.openPaymentsCount > 0 ? 'kpi-warn' : ''}`}>
          <div className="kpi-value">{s.openPaymentsCount}</div>
          <div className="kpi-label">Offene Zahlungen{s.openPaymentsSum > 0 ? ` · ${s.openPaymentsSum.toFixed(2)} €` : ''}</div>
        </div>
        <div className="kpi-card"><div className="kpi-value">{s.totalRegistrations}</div><div className="kpi-label">Anmeldungen gesamt</div></div>
      </div>

      {/* Export */}
      <div className="dash-section">
        <div className="dash-section-head">
          <h3>Mitglieder-Export</h3>
        </div>
        <div className="dash-actions">
          <button className="btn btn-primary" onClick={() => exportMembers('xlsx')}>Excel (.xlsx)</button>
          <button className="btn btn-secondary" onClick={() => exportMembers('csv')}>CSV</button>
        </div>
      </div>

      {/* Auslastung kommender Spiele */}
      <div className="dash-section">
        <div className="dash-section-head"><h3>Auslastung kommender Spiele</h3></div>
        {data.capacity.length === 0 ? (
          <p className="dash-empty">Keine kommenden Spiele.</p>
        ) : (
          <div className="capacity-list">
            {data.capacity.map((g) => {
              const max = g.max_registrations || 50;
              const pct = Math.min(100, Math.round((g.registrations / max) * 100));
              const full = g.registrations >= max;
              return (
                <div className="capacity-row" key={g.id}>
                  <div className="capacity-info">
                    <span className="capacity-name">{g.opponent}</span>
                    <span className="capacity-meta">{fmtDate(g.game_date)} · {g.registrations}/{max}{full ? ' · VOLL' : ''}</span>
                  </div>
                  <div className="capacity-bar">
                    <div className={`capacity-fill ${full ? 'full' : pct >= 80 ? 'high' : ''}`} style={{ width: pct + '%' }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Offene Zahlungen */}
      <div className="dash-section">
        <div className="dash-section-head"><h3>Offene Zahlungen ({data.openPayments.length})</h3></div>
        {data.openPayments.length === 0 ? (
          <p className="dash-empty">Keine offenen Zahlungen. 🎉</p>
        ) : (
          <div className="table-scroll">
            <table className="dash-table">
              <thead>
                <tr><th>Name</th><th>Spiel</th><th>Datum</th><th>Ticket</th><th>Betrag</th><th></th></tr>
              </thead>
              <tbody>
                {data.openPayments.map((r) => (
                  <tr key={r.id}>
                    <td>{r.first_name} {r.last_name}</td>
                    <td>{r.opponent}</td>
                    <td>{fmtDate(r.game_date)}</td>
                    <td>{r.ticket_type}</td>
                    <td>{r.amount != null ? `${Number(r.amount).toFixed(2)} €` : '–'}</td>
                    <td><button className="btn btn-primary btn-sm" onClick={() => markPaid(r.id, true)}>Bezahlt ✓</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Neueste Anmeldungen */}
      <div className="dash-section">
        <div className="dash-section-head"><h3>Neueste Anmeldungen</h3></div>
        {data.recentRegistrations.length === 0 ? (
          <p className="dash-empty">Noch keine Anmeldungen.</p>
        ) : (
          <ul className="recent-list">
            {data.recentRegistrations.map((r) => (
              <li key={r.id}>
                <strong>{r.first_name} {r.last_name}</strong> → {r.opponent}
                <span className="recent-date">{fmtDate(r.created_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default DashboardTab;
